import type { RetrieveInviteResponse } from '@frontend/api-types';
import { some } from '@frontend/duck-tape';
import type { useAccountCreationFormLegacy } from '@frontend/forms';
import { useMemo } from '@frontend/react';
import type { usePromotionTranslation } from '@frontend/react-domain';
import { Banner, Button, PasswordInput, ScrollableContentPanel, TextInput, XStack, YStack } from '@frontend/web-react';
import { useAnalytics } from '@frontend/web/hooks';
import { OnboardingStepHeader } from '../Onboarding/OnboardingStepHeader';

export type CreatePasswordProps = {
  decrementStep: () => void;
  form: ReturnType<typeof useAccountCreationFormLegacy>;
  inviteDetails?: RetrieveInviteResponse;
  isAnyLoading: boolean;
  promotionTranslationActions: ReturnType<typeof usePromotionTranslation>['actions'];
  promotionTranslationState: ReturnType<typeof usePromotionTranslation>['state'];
  searchParamsDescription?: string;
  searchParamsPromoCode?: string;
};

export const CreatePassword = ({
  decrementStep,
  form,
  inviteDetails,
  isAnyLoading,
  promotionTranslationActions,
  promotionTranslationState,
}: CreatePasswordProps) => {
  const { errors, getControl, isValid: isFormValidationPassing, submitForm, values } = form;
  const { firstName, nickname, promoCode } = values;
  const { track } = useAnalytics();

  const needsToClickApply = Boolean(
    !promotionTranslationState.promoCodeErrorMessage &&
      !promotionTranslationState.promotionDescription &&
      !promotionTranslationState.isApplyPromoCodeLoading &&
      promoCode,
  );

  const needsToClearPromo = Boolean(promotionTranslationState.promoCodeErrorMessage && promoCode);

  const isValid = useMemo(
    () =>
      !some([errors.password, errors.confirmPassword, needsToClickApply, needsToClearPromo, !isFormValidationPassing]),
    [errors, needsToClickApply, needsToClearPromo, isFormValidationPassing],
  );

  const entryType: 'accept-invite' | 'signup' = inviteDetails ? 'accept-invite' : 'signup';
  const flowVariant: 'legacy' | 'v2' = 'legacy';
  const platform: 'mobile' | 'web' = 'web';
  const trackingData = {
    entryType,
    flowVariant,
    isHouseholdInvite: Boolean(inviteDetails?.household),
    platform,
  };

  return (
    <ScrollableContentPanel
      containerClassName="bg-cream-50"
      contentWidth="max-w-[488px] w-full"
      footer={
        <Button
          accessibilityLabel='Continue'
          disabled={!isValid || isAnyLoading}
          fullWidth
          isLoading={isAnyLoading}
          label="Continue"
          onClick={submitForm}
          variant="primary"
        />
      }
      header={
        <OnboardingStepHeader
          onClickBackArrow={decrementStep}
          title={`Great, ${nickname || firstName}! Let's make it official.`}
        />
      }
      justifyContent="center"
    >
      <YStack gapY="md">
        <YStack className="justify-center" gapY="xs">
          <PasswordInput
            accessibilityLabel='Password'
            disabled={isAnyLoading}
            label={'Password'}
            labelTextProps={{ className: 'font-medium', type: 'h6' }}
            placeholder="Minimum 12 characters"
            {...getControl('password')}
          />
        </YStack>
        <PasswordInput
          accessibilityLabel='Confirm password'
          disabled={isAnyLoading}
          label={'Confirm password'}
          labelTextProps={{ className: 'font-medium', type: 'h6' }}
          {...getControl('confirmPassword')}
        />

        {/* No promos for household members */}
        {inviteDetails?.household ? null : (
          <YStack gapY="xs">
            <TextInput
              accessibilityLabel='Promo code'
              disabled={isAnyLoading}
              error={
                getControl('promoCode').error ||
                (needsToClickApply ? 'Click apply or leave blank before proceeding.' : undefined)
              }
              label="Promo code (optional)"
              labelTextProps={{ className: 'font-medium', type: 'h6' }}
              onChange={(val) => {
                if (val === '' && Boolean(promoCode.trim())) {
                  track({
                    data: trackingData,
                    event: 'signupPromoCodeRemoved',
                    topic: 'auth',
                  });
                }
                getControl('promoCode').onChange(val);
              }}
              {...getControl('promoCode', ['error', 'onChange'])}
            />
            <XStack justifyContent="end">
              <Button
                accessibilityLabel='Apply promo code'
                disabled={!promoCode.trim() || isAnyLoading}
                isLoading={promotionTranslationState.isApplyPromoCodeLoading}
                label="Apply"
                onClick={async () => {
                  const trimmedPromoCode = promoCode.trim();
                  const promotion = await promotionTranslationActions.handleApplyPromoCode(trimmedPromoCode);
                  if (promotion) {
                    track({
                      data: trackingData,
                      event: 'signupPromoCodeApplied',
                      topic: 'auth',
                    });
                    return;
                  }

                  track({
                    data: {
                      ...trackingData,
                      errorCategory: 'validation',
                      errorCode: 'SIGNUP_PROMO_APPLY_FAILED',
                      errorSource: 'signupPromoApply',
                      retriable: true,
                    },
                    event: 'signupPromoCodeSubmitFailed',
                    topic: 'auth',
                  });
                }}
                size="sm"
                variant="primary"
              />
            </XStack>
          </YStack>
        )}
        <YStack gapY="md">
          {inviteDetails?.household ? null : promotionTranslationState.promoCodeErrorMessage ? (
            <Banner
              color="danger"
              leftIconName="IconAlertCircle"
              title={promotionTranslationState.promoCodeErrorMessage}
            />
          ) : promotionTranslationState.promotionDescription ? (
            <Banner
              color="success"
              leftIconName="IconCheck"
              title={`${promotionTranslationState.promotionDescription}. Redeemable at checkout with applicable plan selection.`}
            />
          ) : null}
        </YStack>
      </YStack>
    </ScrollableContentPanel>
  );
};
